import { Dispatch, SetStateAction, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { FormAutocompleteController, FormInputNumberController } from '@/components';
import {
  getContentLanguage,
  languageSchema,
  RecipeItem,
  RecipeProduct,
  useMenu,
  useProducts,
} from '@/core';

import { styles } from './styles';

type IngredientFormData = {
  product: RecipeProduct;
  amount: number;
};

const schema = z.object({
  product: z.object({
    id: z.number(),
    name: languageSchema,
  }),
  amount: z.number(),
});

export const EditableProduct = ({
  recipeItem,
  recipeItems,
  setRecipeItems,
}: {
  recipeItem: RecipeItem;
  recipeItems: RecipeItem[];
  setRecipeItems: Dispatch<SetStateAction<RecipeItem[]>>;
}) => {
  const { t, i18n } = useTranslation();
  const { useProductsSearch } = useProducts({});
  const [options, setOptions] = useState<RecipeProduct[]>([]);

  const {
    queries: { useMenuRecipe },
  } = useMenu();
  const { id } = useParams<{ id: string }>();
  const { data: recipe } = useMenuRecipe(Number(id), true);

  const methods = useForm<IngredientFormData>({
    defaultValues: {
      product: recipeItem.product,
      amount: recipeItem.amount,
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const handleRemoveIngredientClick = () => {
    setRecipeItems((prev: RecipeItem[]) =>
      prev.filter((el) => el.product.id !== recipeItem.product.id),
    );
  };

  const handleSaveNewIngredient = () => {
    const { amount, product } = methods.getValues();
    if (!product) return;
    setRecipeItems((prev: RecipeItem[]) => {
      const updatedProducts = prev.map((item) => {
        if (item.product.id === recipeItem.product.id) {
          return {
            product,
            amount,
          };
        }
        return item;
      });
      return updatedProducts;
    });
  };

  return (
    <FormProvider {...methods}>
      <TableRow>
        <TableCell align="left">
          <FormAutocompleteController<RecipeProduct>
            label={t('PRODUCT.CHOOSE')}
            controllerName={'product'}
            options={options}
            fetchOptions={useProductsSearch}
            setOptions={setOptions}
            getOptionLabel={(option) => option?.name[getContentLanguage(i18n.language)] ?? ''}
            getOptionKey={(option) => option.id.toString()}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            fieldOnBlur={handleSaveNewIngredient}
            renderInput={() => <div></div>}
            optionsToFilterOut={recipeItems.concat(recipe?.data ?? []).map((el) => el.product)}
          />
        </TableCell>
        <TableCell align="left">
          <Stack direction="row" gap={1} alignItems="center">
            <FormInputNumberController
              label={''}
              controllerName={'amount'}
              fieldOnBlur={handleSaveNewIngredient}
              isWholeNumber
              sx={{ width: '150px' }}
            />
            <Typography variant="body2" color="text.secondary">
              {t('GLOBAL.PIECES_CAPITALIZED')}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="right" sx={styles.shrinkCell}>
          <IconButton size="medium" onClick={handleRemoveIngredientClick}>
            <span className="material-symbols-rounded">close</span>
          </IconButton>
        </TableCell>
      </TableRow>
    </FormProvider>
  );
};
